import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@Redux/detail/store';
import { I_PriceReOrg, PriceStyles, I_MultiPrices } from '@Redux/detail/Group/interfaces';
import cN from 'classnames';
import styles from '../styles.module.scss';
import { useTranslation } from 'react-i18next';
import { ArrowUp } from '@components/Common/Icons';

interface I_PriceItem {
    type: string;
    Price: string;
    PriceOrig: string;
}

const PriceList: React.FC = () => {
    const { PricesInfo } = useSelector((state: RootState) => state.groupReducer);
    return (
        <div className={styles.hotelwrapper}>
            {PricesInfo!.MultiPricesList.map((items: I_MultiPrices, ind: number) => {
                return HotelItem(items, ind);
            })}
        </div>
    );
};

export default PriceList;

/**************/
function HotelItem(items: I_MultiPrices, ind: number) {
    const { curPage } = useSelector((state: RootState) => state.pageReducer);
    const { PricesInfo } = useSelector((state: RootState) => state.groupReducer);

    const isPrint = curPage === 'print';
    // 多房型
    const showMultiPrice =
        !PricesInfo!.IsForeign && PricesInfo!.TourDays > 1 && items.GroupPricesList.length > 1;
    // 國旅一日團
    const isDomesticDayTour = !PricesInfo!.IsForeign && PricesInfo!.TourDays === 1;

    // 第一個預設展開
    const [isClose, setIsClose] = useState<boolean>(!isPrint && ind !== 0);

    return (
        <div
            key={ind}
            className={cN(
                styles['price-list'],
                'price-list',
                { [styles.close]: isClose },
                { [styles.isPrint]: isPrint }
            )}
        >
            <div
                className={cN(styles.hotelname, { [styles.noname]: !items.SaleName })}
                onClick={() => !isPrint && setIsClose(!isClose)}
            >
                <span>{items.SaleName}</span>
                <span>
                    查看費用
                    <ArrowUp />
                </span>
            </div>
            <ul className={`${styles.hotelitem} ${isClose ? styles.hide : ''}`}>
                {isDomesticDayTour
                    ? // 國旅一日遊防呆，若為國旅一日遊僅顯示一個行程費用
                      PriceItemList(
                          items.GroupPricesList[0],
                          isClose,
                          showMultiPrice,
                          isDomesticDayTour
                      )
                    : items.GroupPricesList.map((obj: I_PriceReOrg) =>
                          PriceItemList(obj, isClose, showMultiPrice, isDomesticDayTour)
                      )}
            </ul>
        </div>
    );
}

function PriceItemList(
    item: I_PriceReOrg,
    isClose: boolean,
    showMultiPrice: boolean,
    isDomesticDayTour: boolean
) {
    const { t } = useTranslation();
    const { IsB2E } = useSelector((state: RootState) => state.groupReducer);
    const { curPage } = useSelector((state: RootState) => state.pageReducer);
    const isPrint = curPage === 'print';

    return (
        <li key={item.PriceStyle} className={cN(styles['price-item'], { [styles.hide]: isClose })}>
            <div className={cN(styles['price-item__container--title'], [isPrint ? 'title' : ''])}>
                <span>{showMultiPrice ? PriceStyleToCH(item.PriceStyle) : t('groupFee')}</span>
                {item.PriceName && <div className={styles.comments}>{item.PriceName}</div>}
                {IsB2E && (
                    <div className={styles['price-item__container--title__note']}>(企業價)</div>
                )}
            </div>
            <PriceItem type="大人" PriceOrig={item.AdultsPriceOrig} Price={item.AdultsPrice} />
            <PriceItem
                type={!isDomesticDayTour ? '小孩佔床' : '小孩'}
                PriceOrig={item.ChildrenWithBedOrig}
                Price={item.ChildrenWithBed}
            />
            {!isDomesticDayTour && (
                <>
                    <PriceItem
                        type="小孩不佔床"
                        PriceOrig={item.ChildrenNoPriceOrig}
                        Price={item.ChildrenNoPrice}
                    />
                    <PriceItem
                        type="小孩加床"
                        PriceOrig={item.ChildrenExtraBedOrig}
                        Price={item.ChildrenExtraBed}
                    />
                </>
            )}
            <PriceItem type="嬰兒" PriceOrig={item.BabyPriceOrig} Price={item.BabyPrice} />
        </li>
    );
}

function PriceItem({ type, Price, PriceOrig }: I_PriceItem) {
    const { curPage } = useSelector((state: RootState) => state.pageReducer);
    const isPrint = curPage === 'print';
    return (
        <div className={cN(styles['price-item__container'], [isPrint ? 'container' : ''])}>
            <div className={cN(styles['price-item__type'], [isPrint ? 'type' : ''])}>{type}</div>
            <div className={cN(styles['price-item__price'], [isPrint ? 'sell-price' : ''])}>
                {ShowPrice(Price, PriceOrig)}
            </div>
        </div>
    );
}

function ShowPrice(price: string, PriceOrig: string) {
    const { GroupInfo, PricesInfo, IsB2E } = useSelector((state: RootState) => state.groupReducer);
    const travelType = PricesInfo?.TravelType;
    const status = GroupInfo.Status;
    const sellingPrice = IsB2E ? price : PriceOrig; // 售價
    const formattedPrice = Number(sellingPrice).toLocaleString();
    const dataOrig = IsB2E ? `${PriceOrig === '0' ? '' : PriceOrig}` : '';

    if (sellingPrice === '' || sellingPrice === '0') return '--';

    return (
        <span data-orig={dataOrig}>
            {status == 6
                ? '近期上架'
                : `${formattedPrice}${travelType == 2 ? '起' : ''}`}
        </span>
    );
}

function PriceStyleToCH(style: PriceStyles) {
    switch (style) {
        case PriceStyles.Single:
            return '單人房';
        case PriceStyles.Double:
            return '雙人房';
        case PriceStyles.Triple:
            return '三人房';
        case PriceStyles.Quadruple:
            return '四人房';
    }
}
